import BookStateHelper from '@d-helpers/book-state-helper';
import DropdownOptionsHelper from '@d-helpers/dropdown-options-helper';
import AHttpService from '@d-services/a-http-service/a-http-service';
import { addObjectToFormData } from '@d-shared/forms/forms';
import URLS from '@d-shared/urls/urls';

class AuthorBooksService extends AHttpService {
  async cancelBookReview(bookSlug) {
    const url = URLS.api.author.cancelBookReview.replace('<book-slug>', bookSlug);
    const response = await this._http.get(url);
    return response.data;
  }

  async relistBook(bookSlug) {
    const url = URLS.api.author.relistBook.replace('<book-slug>', bookSlug);
    const response = await this._http.post(url);
    return response.data;
  }

  async getBooks(setState = false) {
    try {
      const response = await this._http.get(URLS.api.author.books);

      if (setState) {
        BookStateHelper.setBooksListState(response.data.books);
      }

      return response.data.books;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        return null;
      }

      throw error;
    }
  }

  async getBookReviewReport(bookSlug) {
    const url = URLS.api.author.reportReview.replace('<book-slug>', bookSlug);
    const response = await this._http.get(url);
    return response.data;
  }

  async getReportReviewReasons(asOptions = false) {
    try {
      const response = await this._http.get(URLS.api.author.reportReviewReasons);

      const reportReviewOptions = response.data.review_report_reasons;
      if (!asOptions) {
        return reportReviewOptions;
      }

      return DropdownOptionsHelper.getListAsOptions(reportReviewOptions);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        return null;
      }

      throw error;
    }
  }

  async removeBook(bookSlug) {
    const url = URLS.api.author.removeBook.replace('<book-slug>', bookSlug);
    const response = await this._http.get(url);
    return response.data;
  }

  async removeBookDraft() {
    const response = await this._http.delete(URLS.api.author.bookDrafts);
    return response.data;
  }

  async removeBookInSeries(fileId) {
    const url = `${ URLS.api.author.bookInSeriesAttachments }/${ fileId }`;
    const response = await this._http.delete(url);
    return response.data;
  }

  async removeCover(fileId) {
    const url = `${ URLS.api.author.images }/${ fileId }`;
    const response = await this._http.delete(url);
    return response.data;
  }

  async removeEpub(fileId) {
    const url = `${ URLS.api.author.epubAttachments }/${ fileId }`;
    const response = await this._http.delete(url);
    return response.data;
  }

  async removeMobi(fileId) {
    const url = `${ URLS.api.author.mobiAttachments }/${ fileId }`;
    const response = await this._http.delete(url);
    return response.data;
  }

  async removeIllustration(fileId) {
    const url = `${ URLS.api.author.illustrationAttachments }/${ fileId }`;
    const response = await this._http.delete(url);
    return response.data;
  }

  async removePdf(fileId) {
    const url = `${ URLS.api.author.pdfAttachments }/${ fileId }`;
    const response = await this._http.delete(url);
    return response.data;
  }

  async reportBookReview(bookSlug, bookData) {
    const formData = new FormData();
    addObjectToFormData(bookData, formData);

    const url = URLS.api.author.reportReview.replace('<book-slug>', bookSlug);
    const response = await this._http.post(url, formData);

    return response.data;
  }

  async resubmitBookForReview(bookSlug, paymentData) {
    const formData = new FormData();
    addObjectToFormData(paymentData, formData);

    const url = URLS.api.author.resubmitBook.replace('<book-slug>', bookSlug);
    const response = await this._http.post(url, formData);
    return response.data;
  }

  async saveBookDraft(bookData) {
    const formData = new FormData();
    addObjectToFormData(bookData, formData);

    const response = await this._http.post(URLS.api.author.bookDrafts, formData);
    return response.data;
  }

  async submitBookForReview(bookData) {
    const formData = new FormData();
    addObjectToFormData(bookData, formData);

    const response = await this._http.post(URLS.api.author.books, formData);
    return response.data;
  }

  async updateBookForReview(bookSlug, bookData) {
    const formData = new FormData();
    addObjectToFormData(bookData, formData);

    const url = URLS.api.author.book.replace('<book-slug>', bookSlug);
    const response = await this._http.patch(url, formData);
    return response.data;
  }

  async uploadBookInSeries(file, position) {
    const uploadData = {
      book_in_series_attachment: {
        attachment: file,
        position,
      },
    };

    const formData = new FormData();
    addObjectToFormData(uploadData, formData);

    const response = await this._http.post(URLS.api.author.bookInSeriesAttachments, formData);
    return response.data;
  }

  async uploadCover(file) {
    const uploadData = {
      image: {
        image: file,
      },
    };

    const formData = new FormData();
    addObjectToFormData(uploadData, formData);

    const response = await this._http.post(URLS.api.author.images, formData);
    return response.data;
  }

  async uploadEpub(file) {
    const uploadData = {
      epub_attachment: {
        attachment: file,
      },
    };

    const formData = new FormData();
    addObjectToFormData(uploadData, formData);

    const response = await this._http.post(URLS.api.author.epubAttachments, formData);
    return response.data;
  }

  async uploadMobi(file) {
    const uploadData = {
      mobi_attachment: {
        attachment: file,
      },
    };

    const formData = new FormData();
    addObjectToFormData(uploadData, formData);

    const response = await this._http.post(URLS.api.author.mobiAttachments, formData);
    return response.data;
  }

  async uploadIllustration(file) {
    const uploadData = {
      illustration_attachment: {
        attachment: file,
      },
    };

    const formData = new FormData();
    addObjectToFormData(uploadData, formData);

    const response = await this._http.post(URLS.api.author.illustrationAttachments, formData);
    return response.data;
  }

  async uploadPdf(file) {
    const uploadData = {
      pdf_attachment: {
        attachment: file,
      },
    };

    const formData = new FormData();
    addObjectToFormData(uploadData, formData);

    const response = await this._http.post(URLS.api.author.pdfAttachments, formData);
    return response.data;
  }
}

export default new AuthorBooksService();
